import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, LogOut, X, Upload, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import {
  verifyAdmin, adminLogout, getAdminUser,
  adminListArticles, adminCreateArticle, adminUpdateArticle, adminDeleteArticle, adminUploadImage, mediaUrl,
} from '../lib/blogApi';

const CATEGORIES = ['General', 'AOG Logistics', 'Freight Market', 'Incoterms', 'Customs Compliance', 'Dangerous Goods', 'Geopolitics'];
const empty = {
  title: '', summary: '', featured_image: '', content: '', category: 'General',
  tags: '', author: 'Aero Time Critical', publication_date: '', seo_title: '', meta_description: '', status: 'draft',
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(empty);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const user = getAdminUser();

  useEffect(() => {
    verifyAdmin().then(() => { setAuthed(true); loadArticles(); })
      .catch(() => { adminLogout(); navigate('/admin/login'); });
    // eslint-disable-next-line
  }, []);

  const loadArticles = async () => {
    try { setArticles(await adminListArticles()); } catch { toast.error('Failed to load articles'); }
  };

  const openNew = () => { setForm(empty); setEditing('new'); setPreview(false); };
  const openEdit = (a) => {
    setForm({ ...a, tags: (a.tags || []).join(', '), publication_date: a.publication_date ? a.publication_date.slice(0, 16) : '' });
    setEditing(a.id); setPreview(false);
  };

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUploadImage(file);
      setForm((f) => ({ ...f, featured_image: res.url }));
      toast.success('Image uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      publication_date: form.publication_date ? new Date(form.publication_date).toISOString() : null,
    };
    delete payload.id; delete payload.slug; delete payload.created_at; delete payload.updated_at;
    try {
      if (editing === 'new') { await adminCreateArticle(payload); toast.success('Article created'); }
      else { await adminUpdateArticle(editing, payload); toast.success('Article updated'); }
      setEditing(null); loadArticles();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const publishNow = async () => {
    setForm((f) => ({ ...f, status: 'published' }));
    toast.info('Set to Published — click Save to confirm');
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this article permanently?')) return;
    try { await adminDeleteArticle(id); toast.success('Deleted'); loadArticles(); } catch { toast.error('Delete failed'); }
  };

  const logout = () => { adminLogout(); navigate('/admin/login'); };

  if (!authed) return <div className="min-h-screen bg-white flex items-center justify-center text-[#4A4A4A]">Verifying...</div>;

  const statusBadge = (s) => {
    const map = { published: 'bg-green-100 text-green-700', draft: 'bg-gray-100 text-gray-600', scheduled: 'bg-amber-100 text-amber-700' };
    return <span className={`px-2 py-1 rounded text-xs font-semibold ${map[s] || map.draft}`}>{s}</span>;
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]">
      {/* Environment banner — warns admins editing on preview that changes won't reach the live site */}
      {typeof window !== 'undefined' && window.location.hostname !== 'aerotimecritical.co.uk' && window.location.hostname !== 'cargo-urgent.emergent.host' && (
        <div className="bg-[#C0392B] text-white text-center text-sm py-2 px-4" data-testid="admin-preview-banner">
          <strong>PREVIEW ENVIRONMENT</strong> — changes made here will <u>not</u> appear on <a href="https://aerotimecritical.co.uk" className="underline font-semibold">aerotimecritical.co.uk</a>. To edit the live site, log in at <a href="https://aerotimecritical.co.uk/admin/login" className="underline font-semibold">aerotimecritical.co.uk/admin/login</a>.
        </div>
      )}
      {/* Top bar */}
      <div className="bg-[#003057] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/atc-logo.png" alt="ATC" className="h-10 w-auto object-contain bg-white rounded px-2 py-1" style={{ maxWidth: '160px' }} />
            <span className="font-semibold">Insights Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-300 hidden sm:inline">{user?.email}</span>
            <Link to="/insights" target="_blank" className="text-sm text-[#00A3E0] flex items-center gap-1">View Site <ExternalLink size={14} /></Link>
            <button onClick={logout} className="flex items-center gap-1 text-sm bg-white/10 px-3 py-2 rounded hover:bg-white/20" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {editing === null ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-[#003057]">Articles ({articles.length})</h1>
              <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="admin-new-article"><Plus size={18} /> New Article</button>
            </div>
            <div className="bg-white rounded-lg border border-[#E5E9EF] overflow-hidden">
              {articles.length === 0 ? (
                <p className="p-8 text-center text-[#4A4A4A]">No articles yet. Create your first article.</p>
              ) : (
                <table className="w-full text-left">
                  <thead className="bg-[#F7F9FB] text-[#003057] text-sm">
                    <tr><th className="p-4">Title</th><th className="p-4 hidden md:table-cell">Category</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
                  </thead>
                  <tbody>
                    {articles.map((a) => (
                      <tr key={a.id} className="border-t border-[#E5E9EF]" data-testid={`admin-row-${a.slug}`}>
                        <td className="p-4 font-semibold text-[#003057]">{a.title}</td>
                        <td className="p-4 hidden md:table-cell text-[#4A4A4A]">{a.category}</td>
                        <td className="p-4">{statusBadge(a.status)}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-3 justify-end">
                            <Link to={`/insights/${a.slug}`} target="_blank" className="text-gray-500 hover:text-[#0089D0]" title="View"><Eye size={18} /></Link>
                            <button onClick={() => openEdit(a)} className="text-gray-500 hover:text-[#0089D0]" title="Edit" data-testid={`admin-edit-${a.slug}`}><Edit size={18} /></button>
                            <button onClick={() => remove(a.id)} className="text-gray-500 hover:text-red-500" title="Delete" data-testid={`admin-delete-${a.slug}`}><Trash2 size={18} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-lg border border-[#E5E9EF] p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-[#003057]">{editing === 'new' ? 'New Article' : 'Edit Article'}</h1>
              <button onClick={() => setEditing(null)} className="text-gray-500 hover:text-red-500"><X size={22} /></button>
            </div>

            <div className="flex gap-3 mb-6">
              <button onClick={() => setPreview(false)} className={`px-4 py-2 rounded text-sm font-semibold ${!preview ? 'bg-[#0089D0] text-white' : 'bg-[#F7F9FB] text-[#003057]'}`}>Edit</button>
              <button onClick={() => setPreview(true)} className={`px-4 py-2 rounded text-sm font-semibold ${preview ? 'bg-[#0089D0] text-white' : 'bg-[#F7F9FB] text-[#003057]'}`} data-testid="admin-preview">Preview</button>
            </div>

            {preview ? (
              <div className="border border-[#E5E9EF] rounded-lg p-6">
                {form.featured_image && <img src={mediaUrl(form.featured_image)} alt="" className="w-full h-64 object-cover rounded mb-6" />}
                <span className="text-xs text-[#0089D0] font-semibold">{form.category}</span>
                <h2 className="text-3xl font-bold text-[#003057] my-3">{form.title || 'Untitled'}</h2>
                <p className="text-lg text-[#003057] border-l-4 border-[#0089D0] pl-4 mb-6">{form.summary}</p>
                <div className="article-body text-[#4A4A4A]" dangerouslySetInnerHTML={{ __html: form.content }} />
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Title *</label>
                  <input name="title" value={form.title} onChange={onChange} data-testid="article-title" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Summary</label>
                  <textarea name="summary" value={form.summary} onChange={onChange} rows={2} data-testid="article-summary" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Featured Image</label>
                  <div className="flex items-center gap-4">
                    {form.featured_image && <img src={mediaUrl(form.featured_image)} alt="" className="h-16 w-24 object-cover rounded border border-[#E5E9EF]" />}
                    <label className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-[#E5E9EF] rounded cursor-pointer hover:bg-[#E5E9EF] text-sm font-semibold text-[#003057]">
                      <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload Image'}
                      <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="article-image-upload" />
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Content (HTML supported)</label>
                  <textarea name="content" value={form.content} onChange={onChange} rows={12} className="font-mono text-sm" data-testid="article-content" placeholder="<p>Write your article here. HTML tags like <h2>, <p>, <ul>, <strong> are supported.</p>" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Category</label>
                    <select name="category" value={form.category} onChange={onChange} data-testid="article-category">
                      {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Author</label>
                    <input name="author" value={form.author} onChange={onChange} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Tags (comma-separated)</label>
                    <input name="tags" value={form.tags} onChange={onChange} placeholder="AOG, aviation, Incoterms" data-testid="article-tags" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Publication Date/Time</label>
                    <input type="datetime-local" name="publication_date" value={form.publication_date} onChange={onChange} data-testid="article-pubdate" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">SEO Title</label>
                    <input name="seo_title" value={form.seo_title} onChange={onChange} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Meta Description</label>
                    <input name="meta_description" value={form.meta_description} onChange={onChange} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Status</label>
                    <select name="status" value={form.status} onChange={onChange} data-testid="article-status">
                      <option value="draft">Draft</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-[#4A4A4A]">Scheduled articles go live automatically at their publication date/time.</p>
              </div>
            )}

            <div className="flex gap-3 mt-8 pt-6 border-t border-[#E5E9EF]">
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="article-save">{saving ? 'Saving...' : 'Save Article'}</button>
              <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
